/**
 * /challenges — screen 3l.
 *
 * Four sections in a fixed order, because the order is the product: what needs
 * a tap from you, what is live, what is open, what is done. Anything demanding
 * a response is lifted out of its natural bucket by the API so the counts stay
 * honest and the nav badge has one number to read (D74).
 *
 * Accept / Decline and Looks-right / Dispute act inline; no navigation.
 *
 * One list, no grid toggle. A grid of challenge cards buys nothing on a phone
 * (the cards are the same width either way) and the toggle cost a 44px control
 * in the filter row, on the one screen whose filters matter.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Challenge, ChallengeLists, ChallengeParticipant } from '@shared/types'
import { useStore } from '../lib/store'
import { ApiFailure, GET, writeOrQueue } from '../lib/api'
import { Body, Card, Eyebrow, Header, Notice, Screen, SkeletonCard, Spacer } from '../ui'
import { ExampleChallenges } from '../ui/ExampleChallenges'

type Scope = 'mine' | 'all'
type StatusFilter = 'live' | 'open' | 'resolved' | null

// ── copy helpers ────────────────────────────────────────────────────────────

function nameOf(p: ChallengeParticipant, meId: string): string {
  return p.userId && p.userId === meId ? 'You' : p.displayName
}

function sideNames(side: ChallengeParticipant[], meId: string): string {
  if (!side.length) return 'nobody yet'
  return side.map((p) => nameOf(p, meId)).join(' + ')
}

function versus(c: Challenge, meId: string): string {
  return `${sideNames(c.sides.a, meId)} vs ${sideNames(c.sides.b, meId)}`
}

/** The other side, from the viewer's seat. */
function opponents(c: Challenge, meId: string): string {
  const mySide = c.viewer.side
  if (!mySide) return versus(c, meId)
  const other = mySide === 'a' ? c.sides.b : c.sides.a
  return sideNames(other, meId)
}

function lockCopy(c: Challenge, now: number): string | null {
  if (c.locksAt === null) return 'no automatic lock'
  if (c.locksAt <= now) return 'locked'
  const mins = Math.round((c.locksAt - now) / 60_000)
  if (mins < 60) return `locks in ${Math.max(1, mins)} min`
  const hours = Math.round(mins / 60)
  if (hours < 36) return `locks in ${hours}h`
  return `locks in ${Math.round(hours / 24)}d`
}

/** One line summarising a finished challenge, written from the viewer's seat. */
function resolvedLine(c: Challenge, meId: string): string {
  const other = opponents(c, meId)
  if (c.status === 'declined') return `${other} declined · ${c.stakeLabel}`
  if (c.status === 'expired') return `Nobody answered in time · ${c.stakeLabel}`
  if (c.status === 'voided') return `Voided · ${c.stakeLabel}`
  if (c.outcome === 'push') return `Push with ${other} · ${c.stakeLabel}`
  if (c.outcome === 'void') return `Called off · ${c.stakeLabel}`
  if (c.viewer.won === true) return `You beat ${other} · ${c.stakeLabel}`
  if (c.viewer.won === false) return `${other} beat you · ${c.stakeLabel}`
  return `${versus(c, meId)} · ${c.stakeLabel}`
}

function resolvedMarker(c: Challenge): { text: string; bright: boolean } {
  if (c.status === 'settled') return { text: '✓ settled', bright: true }
  if (c.status === 'disputed') return { text: '⚠ disputed', bright: false }
  if (c.status === 'declined') return { text: 'declined', bright: false }
  if (c.status === 'expired') return { text: 'expired', bright: false }
  if (c.status === 'voided') return { text: 'voided', bright: false }
  if (c.outcome === 'push' || c.outcome === 'void') return { text: 'no debt', bright: false }
  return { text: '⏳ unsettled', bright: false }
}

// ── screen ──────────────────────────────────────────────────────────────────

export default function Challenges() {
  const { state, toast, refresh } = useStore()
  const navigate = useNavigate()
  const meId = state?.user.id ?? ''

  const [scope, setScope] = useState<Scope>('all')
  const [status, setStatus] = useState<StatusFilter>(null)
  const [lists, setLists] = useState<ChallengeLists | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(t)
  }, [])

  const query = useMemo(() => {
    const p = new URLSearchParams({ scope })
    if (status) p.set('status', status)
    return p.toString()
  }, [scope, status])

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      setLists(await GET<ChallengeLists>(`/api/challenges?${query}`, signal))
      setErr(null)
    } catch (e) {
      if (signal?.aborted) return
      // Keep whatever is on screen; say what happened (D103).
      setErr(e instanceof ApiFailure ? e.message : 'Couldn’t refresh challenges.')
    }
  }, [query])

  useEffect(() => {
    const ac = new AbortController()
    void load(ac.signal)
    return () => ac.abort()
  }, [load])

  const act = useCallback(async (
    id: string,
    action: 'accept' | 'decline' | 'confirm' | 'dispute',
    label: string,
  ) => {
    setBusyId(id)
    try {
      const res = await writeOrQueue(`/api/challenges/${id}/${action}`, 'POST', {}, label)
      if (res === null) toast('Saved. It will send when you’re back online')
      await load()
      void refresh(true)
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : 'Couldn’t record that.')
    } finally {
      setBusyId(null)
    }
  }, [load, refresh, toast])

  const counts = lists?.counts
  const nothing = lists
    && !lists.needsYou.length && !lists.live.length && !lists.open.length && !lists.resolved.length

  return (
    <Screen style={{ flex: '1 1 auto', minHeight: 0 }}>
      <Header
        bordered={false}
        // `title` rather than `left`: the Header centres a title on the row and
        // leaves the badge and the action where they are, which is how every
        // other screen in the app is headed.
        title={<span className="screen-title">💪 Feats</span>}
        right={
          <button
            onClick={() => navigate('/challenges/new')}
            style={{
              fontSize: 12, fontWeight: 700, padding: '10px 16px', borderRadius: 999, minHeight: 44,
              background: 'var(--fill)', color: 'var(--ink)', flex: '0 0 auto',
            }}
          >
            + New
          </button>
        }
        // The chips scroll horizontally rather than wrapping, so a long filter
        // row never pushes the list down a line on a narrow phone.
        sub={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <div
              className="scr"
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                flex: '1 1 auto', minWidth: 0, overflowX: 'auto', paddingBottom: 2,
              }}
            >
              <FilterChip label="All" on={scope === 'all'} onClick={() => setScope('all')} />
              <FilterChip label="Mine" on={scope === 'mine'} onClick={() => setScope('mine')} />
              <span style={{ width: 10, flex: '0 0 auto' }} />
              <FilterChip label="Live" on={status === 'live'} onClick={() => setStatus(status === 'live' ? null : 'live')} />
              <FilterChip label="Open" on={status === 'open'} onClick={() => setStatus(status === 'open' ? null : 'open')} />
              <FilterChip label="Resolved" on={status === 'resolved'} onClick={() => setStatus(status === 'resolved' ? null : 'resolved')} />
            </div>
          </div>
        }
      />

      <Body style={{ padding: '6px var(--gutter) calc(24px + var(--nav-h))' }}>
        {err ? <><Notice tone="warn">{err}</Notice><Spacer h={14} /></> : null}

        {lists === null ? (
          <>
            <SkeletonCard lines={4} />
            <Spacer h={12} />
            <SkeletonCard lines={3} />
            <Spacer h={12} />
            <SkeletonCard lines={3} />
          </>
        ) : nothing ? (
          <Card style={{ padding: '26px 18px', textAlign: 'center' }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>No challenges yet.</div>
            <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 6 }}>Someone has to go first.</div>
            <button
              onClick={() => navigate('/challenges/new')}
              style={{
                marginTop: 14, padding: '11px 20px', borderRadius: 999, minHeight: 44,
                fontSize: 13, fontWeight: 700, background: 'var(--fill)', color: 'var(--ink)',
              }}
            >
              Start one
            </button>
          </Card>
        ) : null}

        {lists !== null && !nothing ? (
          <>
            {lists.needsYou.length ? (
              <>
                <Eyebrow style={{ color: 'var(--bright)' }}>Needs you · {counts?.needsYou ?? lists.needsYou.length}</Eyebrow>
                <div>
                  {lists.needsYou.map((c) => (
                    <NeedsYouCard
                      key={c.id}
                      challenge={c}
                      meId={meId}
                      busy={busyId === c.id}
                      onOpen={() => navigate(`/challenges/${c.id}`)}
                      onPrimary={() => void act(
                        c.id,
                        c.viewer.needsAccept ? 'accept' : 'confirm',
                        c.viewer.needsAccept ? 'Accepted a challenge' : 'Confirmed an outcome',
                      )}
                      onSecondary={() => void act(
                        c.id,
                        c.viewer.needsAccept ? 'decline' : 'dispute',
                        c.viewer.needsAccept ? 'Declined a challenge' : 'Disputed an outcome',
                      )}
                    />
                  ))}
                </div>
              </>
            ) : null}

            {lists.live.length ? (
              <>
                <Eyebrow style={{ marginTop: lists.needsYou.length ? 22 : 0 }}>
                  Live · {counts?.live ?? lists.live.length}
                </Eyebrow>
                <div>
                  {lists.live.map((c) => (
                    <LiveCard
                      key={c.id}
                      challenge={c}
                      meId={meId}
                      onOpen={() => navigate(`/challenges/${c.id}`)}
                    />
                  ))}
                </div>
              </>
            ) : null}

            {lists.open.length ? (
              <>
                <Eyebrow style={{ marginTop: lists.needsYou.length || lists.live.length ? 22 : 0 }}>
                  Open · {counts?.open ?? lists.open.length}
                </Eyebrow>
                <div>
                  {lists.open.map((c) => (
                    <OpenCard
                      key={c.id}
                      challenge={c}
                      meId={meId}
                      now={now}
                      onOpen={() => navigate(`/challenges/${c.id}`)}
                    />
                  ))}
                </div>
              </>
            ) : null}

            {lists.resolved.length ? (
              <>
                <Eyebrow style={{ marginTop: 22 }}>Resolved · {counts?.resolved ?? lists.resolved.length}</Eyebrow>
                {lists.resolved.map((c) => {
                  const marker = resolvedMarker(c)
                  return (
                    <button
                      key={c.id}
                      onClick={() => navigate(`/challenges/${c.id}`)}
                      style={{
                        display: 'flex', width: '100%', justifyContent: 'space-between', gap: 12,
                        padding: '12px 0', borderBottom: '1px solid var(--line)',
                        fontSize: 13, textAlign: 'left', color: 'var(--fg)', minHeight: 44,
                      }}
                    >
                      <span style={{ minWidth: 0 }}>{resolvedLine(c, meId)}</span>
                      <span style={{ flex: '0 0 auto', color: marker.bright ? 'var(--bright)' : 'var(--dim)' }}>
                        {marker.text}
                      </span>
                    </button>
                  )
                })}
              </>
            ) : null}
          </>
        ) : null}

        {/* Always last: the examples are the floor of this screen, whether the
            room has run a hundred challenges or none. */}
        {lists !== null ? <ExampleChallenges style={{ marginTop: 26 }} /> : null}
      </Body>
    </Screen>
  )
}

// ── chips ───────────────────────────────────────────────────────────────────

function FilterChip({ label, on, onClick }: { label: string; on: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      aria-pressed={on}
      className="mono"
      style={{
        fontSize: 11, fontWeight: 600, padding: '8px 12px', borderRadius: 999, minHeight: 44,
        background: on ? 'var(--fill)' : 'transparent',
        color: on ? 'var(--ink)' : 'var(--dim)',
        border: on ? '1px solid var(--fill)' : '1px solid var(--line)',
      }}
    >
      {label}
    </button>
  )
}

// ── cards ───────────────────────────────────────────────────────────────────

function NeedsYouCard({
  challenge: c, meId, busy, onOpen, onPrimary, onSecondary,
}: {
  challenge: Challenge
  meId: string
  busy: boolean
  onOpen: () => void
  onPrimary: () => void
  onSecondary: () => void
}) {
  const accepting = c.viewer.needsAccept
  const from = accepting
    ? `${c.creatorName} → You`
    : `${c.resolvedByName ?? 'The feed'} called it`

  return (
    <Card style={{ marginTop: 10 }}>
      <button onClick={onOpen} style={{ display: 'block', width: '100%', textAlign: 'left', color: 'var(--fg)' }}>
        <div style={{ fontSize: 12, color: 'var(--dim)' }}>{from}</div>
        <div style={{ fontSize: 15, fontWeight: 700, marginTop: 6, textWrap: 'balance' }}>
          “{c.proposition}”
        </div>
        <div style={{ fontSize: 12, color: 'var(--dim)', marginTop: 4 }}>
          {c.stakeLabel}{c.gameLabel ? ` · ${c.gameLabel}` : ''}
        </div>
      </button>

      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button
          onClick={onPrimary}
          disabled={busy}
          style={{
            flex: 1, textAlign: 'center', padding: 10, borderRadius: 10, minHeight: 44,
            background: 'var(--fill)', color: 'var(--ink)', fontSize: 13, fontWeight: 600,
            opacity: busy ? 0.6 : 1,
          }}
        >
          {busy ? '…' : accepting ? 'Accept' : 'Looks right'}
        </button>
        <button
          onClick={onSecondary}
          disabled={busy}
          style={{
            flex: 1, textAlign: 'center', padding: 10, borderRadius: 10, minHeight: 44,
            border: '1px solid var(--line)', color: 'var(--dim)', fontSize: 13, fontWeight: 600,
            opacity: busy ? 0.6 : 1,
          }}
        >
          {accepting ? 'Decline' : 'Dispute'}
        </button>
      </div>
    </Card>
  )
}

function LiveCard({
  challenge: c, meId, onOpen,
}: {
  challenge: Challenge
  meId: string
  onOpen: () => void
}) {
  const auto = c.resolutionSrc === 'auto'
  const tail = c.status === 'resolved'
    ? c.viewer.won === true ? 'resolved, you win'
      : c.viewer.won === false ? 'resolved, they win'
        : 'resolved'
    : c.isLocked ? 'live now' : 'waiting to lock'

  return (
    <Card style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
        <span style={{ fontSize: 15, fontWeight: 700, minWidth: 0 }}>
          {auto ? '⚡ ' : ''}{versus(c, meId)}
        </span>
        <span style={{ fontSize: 12, color: 'var(--dim)', flex: '0 0 auto' }}>{c.stakeLabel}</span>
      </div>
      <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 6 }}>
        “{c.proposition}” · {tail}
      </div>
      <button
        onClick={onOpen}
        style={{
          display: 'block', width: '100%', marginTop: 12, textAlign: 'center',
          padding: 10, borderRadius: 10, minHeight: 44,
          background: 'var(--fill)', color: 'var(--ink)', fontSize: 13, fontWeight: 600,
        }}
      >
        View
      </button>
    </Card>
  )
}

function OpenCard({
  challenge: c, meId, now, onOpen,
}: {
  challenge: Challenge
  meId: string
  now: number
  onOpen: () => void
}) {
  const lock = lockCopy(c, now)
  return (
    <Card onClick={onOpen} style={{ marginTop: 10 }}>
      <div style={{ fontSize: 13, color: 'var(--dim)' }}>
        {versus(c, meId)}{c.isPublic ? '' : ' 🔒 private'}
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, marginTop: 6, textWrap: 'balance' }}>
        “{c.proposition}”
      </div>
      <div style={{ fontSize: 12, color: 'var(--dim)', marginTop: 4 }}>
        {c.stakeLabel}{lock ? ` · ${lock}` : ''}
      </div>
    </Card>
  )
}
